import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { GlobalMethodInfo, MethodConfigField, MethodEnvironmentInfo } from '../../core/models';

// THE PLATFORM CATALOGUE AND ITS ENVIRONMENTS.
//
// A payment method is its LOGIC plus its FORM — the field structure that logic needs, declared by the
// adapter and served by the API so this page stays payment-agnostic. An ENVIRONMENT is that form filled in
// and named; a merchant's assignment picks one. So this page manages two things:
//
//   * the method itself — display name, availability to merchants (the kill switch);
//   * its environments — create from the form, fill, mark production, delete.
//
// Write-only throughout: a stored value is never displayed, only whether one is set.
@Component({
    selector: 'app-global-payment-methods',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, DialogModule, MessageModule, ToggleSwitchModule, SelectButtonModule, InputTextModule, TooltipModule, FormsModule, DatePipe],
    template: `
        <p-card>
            <ng-template pTemplate="title"><span>Payment Methods<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Each method has named environments; a merchant is assigned to one of them." tooltipPosition="top"></i></span></ng-template>

            <p-table [value]="methods()" [loading]="loading()">
                <ng-template pTemplate="header">
                    <tr><th>Code</th><th>Name</th><th>Available to merchants</th><th>Config version</th><th>Updated</th><th></th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-m>
                    <tr class="cursor-pointer" (click)="open(m)">
                        <td class="font-medium">{{ m.code }}</td>
                        <td>{{ m.displayName }}</td>
                        <td><p-tag [value]="m.enabled ? 'Enabled' : 'Disabled'" [severity]="m.enabled ? 'success' : 'secondary'" /></td>
                        <td>v{{ m.version }}</td>
                        <td class="text-sm text-surface-500">{{ m.lastModified ? (m.lastModified | date: 'short') : '—' }}</td>
                        <td><p-button icon="pi pi-cog" [text]="true" size="small" (onClick)="open(m)" /></td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="6">{{ error() ?? 'No methods in the catalogue yet.' }}</td></tr>
                </ng-template>
            </p-table>
        </p-card>

        <!-- ============================ METHOD + ENVIRONMENTS ============================ -->
        <p-dialog [(visible)]="showMethod" [modal]="true" [style]="{ width: '58rem' }" [dismissableMask]="true"
                  [header]="'Payment method — ' + (editingMethod()?.code ?? '')">
            @if (editingMethod(); as m) {
                <div class="flex flex-col gap-5">
                    <div class="flex flex-wrap items-end gap-4">
                        <div class="grow">
                            <label class="block text-sm mb-1">Display name</label>
                            <input pInputText class="w-full" [(ngModel)]="methodForm.displayName" />
                        </div>
                        <div class="flex items-center gap-2 pb-2">
                            <p-toggleswitch [(ngModel)]="methodForm.enabled" />
                            <span class="text-sm">Available to merchants</span>
                        </div>
                        <p-button label="Save method" icon="pi pi-check" size="small" [loading]="methodSaving()" (onClick)="saveMethod()" />
                    </div>
                    @if (methodSaveError()) { <p-message severity="error">{{ methodSaveError() }}</p-message> }

                    <div>
                        <div class="flex items-center justify-between mb-1">
                            <h6 class="m-0">Environments<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Sandbox environments should use the processor's test account. Merchants go live on a production environment." tooltipPosition="top"></i></h6>
                            <p-button icon="pi pi-plus" label="New environment" size="small" [text]="true" (onClick)="addEnvironment()" />
                        </div>

                        @if (environmentsLoading()) {
                            <p class="text-sm text-surface-500">Loading environments…</p>
                        } @else {
                            <p-table [value]="environments()">
                                <ng-template pTemplate="header">
                                    <tr><th>Name</th><th>Kind</th><th>In use by</th><th>Version</th><th>Updated</th><th></th></tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-e>
                                    <tr>
                                        <td class="font-medium">{{ e.name }}</td>
                                        <td><p-tag [value]="e.isProduction ? 'PRODUCTION' : 'SANDBOX'" [severity]="e.isProduction ? 'success' : 'warn'" /></td>
                                        <td>{{ e.assignmentCount }} location(s)</td>
                                        <td>v{{ e.version }}</td>
                                        <td class="text-sm text-surface-500">{{ e.lastModified ? (e.lastModified | date: 'short') : '—' }}</td>
                                        <td class="whitespace-nowrap">
                                            <p-button icon="pi pi-pencil" [text]="true" size="small" (onClick)="editEnvironment(e)" />
                                            <!-- Deleting one that is in use is refused by the server with a count; the
                                                 button is hidden rather than offering an action that cannot succeed. -->
                                            @if (e.assignmentCount === 0) {
                                                <p-button icon="pi pi-trash" [text]="true" severity="danger" size="small"
                                                          [loading]="deleting() === e.id" (onClick)="removeEnvironment(e)" />
                                            }
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="emptymessage">
                                    <tr><td colspan="6">No environments yet — create one to configure this method.</td></tr>
                                </ng-template>
                            </p-table>
                        }
                        @if (environmentsError()) { <p-message severity="error" styleClass="mt-2">{{ environmentsError() }}</p-message> }
                    </div>
                </div>
            }
        </p-dialog>

        <!-- ============================ ONE ENVIRONMENT ============================ -->
        <p-dialog [(visible)]="showEnvironment" [modal]="true" [style]="{ width: '46rem' }" [dismissableMask]="true"
                  [header]="(editingEnvironment() ? 'Edit environment' : 'New environment') + ' — ' + (editingMethod()?.code ?? '')">
            <div class="flex flex-col gap-4">
                <div>
                    <label class="block text-sm mb-1">Name<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Shown when assigning a merchant, so use a name others will recognize." tooltipPosition="top"></i></label>
                    <input pInputText class="w-full" [(ngModel)]="envForm.name" placeholder="e.g. Acme Liquor — Production" />
                </div>

                <div class="flex items-center gap-2">
                    <p-toggleswitch [ngModel]="envForm.isProduction" (ngModelChange)="onProductionChange($event)" />
                    <span class="text-sm">Production</span>
                    <p-tag [value]="envForm.isProduction ? 'PRODUCTION' : 'SANDBOX'" [severity]="envForm.isProduction ? 'success' : 'warn'" />
                </div>
                @if (envForm.isProduction) {
                    <p-message severity="info" styleClass="text-xs">
                        Every required field (*) must be filled. Each merchant using this environment also needs its own MID.
                    </p-message>
                } @else {
                    <!-- The fill is now the ONLY thing making a sandbox environment safe, so the screen says
                         so where the fields are typed rather than leaving it to a runtime override. -->
                    <p-message severity="warn" styleClass="text-xs">
                        Enter the processor's <b>test</b> gateway URL and account below. The credentials entered here are the ones used.
                    </p-message>
                }

                <div>
                    <div class="flex items-center justify-between mb-1">
                        <label class="text-sm font-medium">Configuration<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Blank fields keep their stored value. Stored values are never displayed." tooltipPosition="top"></i></label>
                        <p-selectbutton [options]="cfgViews" [(ngModel)]="cfgView" optionLabel="label" optionValue="value" [allowEmpty]="false" size="small" />
                    </div>

                    @if (cfgView === 'form') {

                        <!-- SHARED FIELDS: what every merchant on this environment uses. -->
                        @for (f of sharedFields(); track f.key) {
                            <div class="mb-2">
                                <label class="block text-xs text-surface-600 dark:text-surface-300 mb-1">
                                    {{ f.label }}
                                    @if (f.requiredForProduction) { <span class="text-primary font-semibold">*</span> }
                                    @if (f.configured) {
                                        <p-tag value="Set" severity="success" styleClass="text-[10px] ml-1" />
                                    } @else {
                                        <p-tag value="Not set" severity="secondary" styleClass="text-[10px] ml-1" />
                                    }
                                    @if (f.help) { <span class="text-surface-400"> — {{ f.help }}</span> }
                                </label>
                                <input pInputText [type]="f.secret ? 'password' : 'text'" class="w-full"
                                       [(ngModel)]="fieldValues[f.key]" [name]="'env-' + f.key"
                                       [placeholder]="f.configured ? (f.secret ? '•••••• (unchanged)' : '(unchanged)') : (f.defaultValue ?? '')" />
                            </div>
                        }

                        <!-- MERCHANT FIELDS: DEFAULTS. A merchant assigned here either takes these or
                             overrides them with its own on its merchant page — which is why they are not
                             required to make this environment production. -->
                        @if (merchantDefaultFields().length) {
                            <div class="mt-4 pt-3 border-t border-surface-200 dark:border-surface-700">
                                <div class="text-xs font-semibold mb-1">Merchant defaults<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Optional. Used by merchants on this environment that do not set their own values." tooltipPosition="top"></i></div>
                                @for (f of merchantDefaultFields(); track f.key) {
                                    <div class="mb-2">
                                        <label class="block text-xs text-surface-600 dark:text-surface-300 mb-1">
                                            {{ f.label }}
                                            @if (f.configured) {
                                                <p-tag value="Set" severity="success" styleClass="text-[10px] ml-1" />
                                            } @else {
                                                <p-tag value="Not set" severity="secondary" styleClass="text-[10px] ml-1" />
                                            }
                                        </label>
                                        <input pInputText [type]="f.secret ? 'password' : 'text'" class="w-full"
                                               [(ngModel)]="fieldValues[f.key]" [name]="'env-' + f.key"
                                               [placeholder]="f.configured ? (f.secret ? '•••••• (unchanged)' : '(unchanged)') : (f.defaultValue ?? '')" />
                                    </div>
                                }
                            </div>
                        }

                        @if (!formFields().length) {
                            <p-message severity="info" styleClass="mb-2">No form for this method — use the JSON view.</p-message>
                        }
                    } @else {
                        <textarea [(ngModel)]="envForm.configJson" rows="10" spellcheck="false"
                                  class="w-full font-mono text-xs p-2 border border-surface-300 dark:border-surface-700 rounded bg-surface-0 dark:bg-surface-900"
                                  placeholder="(unchanged)"></textarea>
                    }
                </div>

                @if (envSaveError()) { <p-message severity="error">{{ envSaveError() }}</p-message> }
                <div class="flex justify-end gap-2">
                    <p-button label="Cancel" [text]="true" (onClick)="showEnvironment.set(false)" />
                    <p-button label="Save" icon="pi pi-check" [loading]="envSaving()" (onClick)="saveEnvironment()" />
                </div>
            </div>
        </p-dialog>
    `
})
export class GlobalPaymentMethods implements OnInit {
    private readonly api = inject(MiddlewareApi);

    readonly methods = signal<GlobalMethodInfo[]>([]);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);

    // --- method dialog ---
    readonly showMethod = signal(false);
    readonly editingMethod = signal<GlobalMethodInfo | null>(null);
    readonly methodSaving = signal(false);
    readonly methodSaveError = signal<string | null>(null);
    methodForm: { displayName: string; enabled: boolean } = { displayName: '', enabled: true };

    // --- environments of the open method ---
    readonly environments = signal<MethodEnvironmentInfo[]>([]);
    readonly environmentsLoading = signal(false);
    readonly environmentsError = signal<string | null>(null);
    readonly deleting = signal<string | null>(null);

    // --- environment dialog ---
    readonly showEnvironment = signal(false);
    readonly editingEnvironment = signal<MethodEnvironmentInfo | null>(null);
    readonly envSaving = signal(false);
    readonly envSaveError = signal<string | null>(null);
    readonly formFields = signal<MethodConfigField[]>([]);
    // isProduction lives on the object AND in a signal, because the "* required" hints and the warning
    // banner derive from it and the app is zoneless.
    readonly isProduction = signal(false);
    envForm: { name: string; isProduction: boolean; configJson: string } = { name: '', isProduction: false, configJson: '' };
    fieldValues: Record<string, string> = {};
    cfgView: 'form' | 'json' = 'form';
    readonly cfgViews = [
        { label: 'Form', value: 'form' },
        { label: 'JSON', value: 'json' }
    ];

    // The form, split the way the model splits it. Both derive from what the API served — this page never
    // names a field, so a future gateway's form renders here with no change at all.
    readonly sharedFields = computed(() => this.formFields().filter((f) => !f.merchantSpecific));
    readonly merchantDefaultFields = computed(() => this.formFields().filter((f) => f.merchantSpecific));

    ngOnInit(): void {
        this.load();
    }

    private load(): void {
        this.loading.set(true);
        this.api.getGlobalMethods().subscribe({
            next: (m) => { this.methods.set(m ?? []); this.loading.set(false); },
            error: (e) => { this.error.set(this.describe(e)); this.loading.set(false); }
        });
    }

    open(m: GlobalMethodInfo): void {
        this.editingMethod.set(m);
        this.methodForm = { displayName: m.displayName, enabled: m.enabled };
        this.methodSaveError.set(null);
        this.loadEnvironments(m.code);
        this.showMethod.set(true);
    }

    private loadEnvironments(code: string): void {
        this.environments.set([]);
        this.environmentsError.set(null);
        this.environmentsLoading.set(true);
        this.api.getMethodEnvironments(code).subscribe({
            next: (e) => { this.environments.set(e ?? []); this.environmentsLoading.set(false); },
            error: (e) => { this.environmentsError.set(this.describe(e)); this.environmentsLoading.set(false); }
        });
    }

    saveMethod(): void {
        const m = this.editingMethod();
        if (!m) return;
        if (!this.methodForm.displayName.trim()) { this.methodSaveError.set('Display name is required.'); return; }

        this.methodSaving.set(true);
        this.methodSaveError.set(null);
        // configJson null: the catalogue row's own config (the platform dev identity and the legacy
        // resolution base) is not edited from here — environments are where configuration lives now.
        this.api.upsertGlobalMethod(m.code, { displayName: this.methodForm.displayName.trim(), enabled: this.methodForm.enabled, configJson: null }).subscribe({
            next: () => { this.methodSaving.set(false); this.load(); },
            error: (e) => { this.methodSaving.set(false); this.methodSaveError.set(this.describe(e)); }
        });
    }

    // ---------------------------------------------------------------------------------------------
    // Environments
    // ---------------------------------------------------------------------------------------------

    addEnvironment(): void {
        const m = this.editingMethod();
        if (!m) return;

        this.editingEnvironment.set(null);
        this.envForm = { name: '', isProduction: false, configJson: '' };
        this.isProduction.set(false);
        this.fieldValues = {};
        this.cfgView = 'form';
        this.envSaveError.set(null);
        this.formFields.set([]);

        // The blank form, plus its defaults — a new environment starts from the method's declared shape.
        this.api.getMethodForm(m.code).subscribe({
            next: (fields) => {
                this.formFields.set(fields ?? []);
                const seeded: Record<string, string> = {};
                for (const f of fields ?? []) if (f.defaultValue) seeded[f.key] = f.defaultValue;
                this.fieldValues = seeded;
            },
            error: () => { this.formFields.set([]); this.cfgView = 'json'; }
        });

        this.showEnvironment.set(true);
    }

    editEnvironment(e: MethodEnvironmentInfo): void {
        const m = this.editingMethod();
        if (!m) return;

        this.editingEnvironment.set(e);
        this.envForm = { name: e.name, isProduction: e.isProduction, configJson: '' };
        this.isProduction.set(e.isProduction);
        this.fieldValues = {};
        this.cfgView = 'form';
        this.envSaveError.set(null);
        this.formFields.set([]);

        // The form WITH per-field presence — the vault's entire read surface.
        this.api.getMethodEnvironmentForm(m.code, e.id).subscribe({
            next: (fields) => this.formFields.set(fields ?? []),
            error: () => { this.formFields.set([]); this.cfgView = 'json'; }
        });

        this.showEnvironment.set(true);
    }

    onProductionChange(isProduction: boolean): void {
        this.envForm.isProduction = isProduction;
        this.isProduction.set(isProduction);
    }

    saveEnvironment(): void {
        const m = this.editingMethod();
        if (!m) return;
        if (!this.envForm.name.trim()) { this.envSaveError.set('An environment needs a name.'); return; }

        let config: string | null = null;
        if (this.cfgView === 'form' && this.formFields().length) {
            // Build JSON from non-empty fields only — the server merges over stored values, so a blank
            // field means "unchanged" rather than "erase".
            const obj: Record<string, string> = {};
            for (const f of this.formFields()) {
                const v = (this.fieldValues[f.key] ?? '').trim();
                if (v) obj[f.key] = v;
            }
            config = Object.keys(obj).length ? JSON.stringify(obj) : null;
        } else {
            const raw = this.envForm.configJson.trim();
            if (raw) {
                try { JSON.parse(raw); } catch { this.envSaveError.set('Configuration is not valid JSON.'); return; }
                config = raw;
            }
        }

        this.envSaving.set(true);
        this.envSaveError.set(null);

        const body = { name: this.envForm.name.trim(), isProduction: this.envForm.isProduction, configJson: config };
        const existing = this.editingEnvironment();
        // Create answers the new id, update answers 204 — neither is used here (the list is reloaded), so
        // the two are widened to one observable rather than branched over twice.
        const request: Observable<unknown> = existing
            ? this.api.updateMethodEnvironment(m.code, existing.id, body)
            : this.api.createMethodEnvironment(m.code, body);

        request.subscribe({
            next: () => {
                this.envSaving.set(false);
                this.showEnvironment.set(false);
                this.loadEnvironments(m.code);
            },
            // The refusals that matter land here: a duplicate name, and "cannot be a production
            // environment yet — it is missing X, Y". Inline, verbatim from the server.
            error: (err: unknown) => { this.envSaving.set(false); this.envSaveError.set(this.describe(err)); }
        });
    }

    removeEnvironment(e: MethodEnvironmentInfo): void {
        const m = this.editingMethod();
        if (!m) return;

        this.deleting.set(e.id);
        this.environmentsError.set(null);
        this.api.deleteMethodEnvironment(m.code, e.id).subscribe({
            next: () => { this.deleting.set(null); this.loadEnvironments(m.code); },
            error: (err) => { this.deleting.set(null); this.environmentsError.set(this.describe(err)); }
        });
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the server.';
        if (e?.status === 403) return 'Forbidden — managing the catalogue needs the payments:manage permission.';
        const server = e?.error?.detail ?? e?.error?.message ?? e?.error?.error;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
